import { supabase } from './supabase';
import {
  createInvoice,
  type InvoiceItemInput,
  type InvoicePayload,
} from './invoiceUtils';
import {
  isConsumptionCategoryItem,
  priceListLineTotalAmount,
  PRICE_LIST_CONSUMPTION_LABEL,
} from './priceListItemPricing';
import { normalizePriceListItems } from './priceListCustomLines';
import type { Database } from '../types/database.types';

type InvoiceRow = Database['public']['Tables']['invoices']['Row'];

export interface InstallmentInput {
  percentage: number;
  due_days: number;
  label?: string | null;
}

export interface DepositBaseBreakdown {
  products: number;
  canone: number;
  base: number;
}

const isCanoneItem = (item: { products?: { category?: string | null } | null }): boolean =>
  (item.products?.category || '').trim().toLowerCase() === 'canone';

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Recupera offerta + items + customer da DB.
 */
export const fetchPriceListForInvoicing = async (priceListId: string) => {
  const { data, error } = await supabase
    .from('price_lists')
    .select(`
      *,
      price_list_items (
        *,
        products (*)
      )
    `)
    .eq('id', priceListId)
    .single();

  if (error || !data) {
    throw new Error(`Offerta non trovata: ${error?.message ?? 'sconosciuto'}`);
  }

  // Le righe manuali (product_id null) espongono un prodotto sintetico dai campi custom_*
  return {
    ...(data as any),
    price_list_items: normalizePriceListItems((data as any).price_list_items),
  } as any;
};

/**
 * Base imponibile per fattura da offerta (acconto o totale).
 * Include prodotti fissi + canone × canone_months. Esclude voci "a consumo".
 * Se `includeCanone: false`, i canoni sono esclusi dalla base (utile per acconti solo sui prodotti).
 */
export const computeDepositBase = (
  priceList: any,
  options: { includeCanone?: boolean } = {}
): DepositBaseBreakdown => {
  const includeCanone = options.includeCanone !== false;
  const items: any[] = priceList.price_list_items || [];
  const canoneMonths: number = priceList.canone_months ?? 1;

  let products = 0;
  let canoneMonthly = 0;

  for (const item of items) {
    if (isConsumptionCategoryItem(item)) continue;
    const lineAmount = priceListLineTotalAmount(item);
    if (isCanoneItem(item)) {
      canoneMonthly += lineAmount;
    } else {
      products += lineAmount;
    }
  }

  const canone = includeCanone ? canoneMonthly * canoneMonths : 0;
  return {
    products: round2(products),
    canone: round2(canone),
    base: round2(products + canone),
  };
};

/**
 * Valida un piano rate: somma percentuali = 100, almeno una rata, percentuali positive.
 */
export const validateInstallments = (plan: InstallmentInput[]): string | null => {
  if (!plan || plan.length === 0) return 'Almeno una rata richiesta.';
  const sum = plan.reduce((acc, r) => acc + r.percentage, 0);
  if (Math.abs(sum - 100) > 0.01) return `Somma percentuali = ${sum.toFixed(2)}%, deve essere 100%.`;
  for (const r of plan) {
    if (r.percentage <= 0) return 'Le percentuali devono essere positive.';
    if (r.due_days < 0) return 'I giorni di scadenza non possono essere negativi.';
  }
  return null;
};

/**
 * Distribuisce un totale su un piano rate. L'ultima rata assorbe il resto per pareggiare al centesimo.
 */
export const computeInstallmentAmounts = (
  totalAmount: number,
  plan: InstallmentInput[]
): number[] => {
  const amounts = plan.map((r) => round2((totalAmount * r.percentage) / 100));
  const diff = round2(totalAmount - amounts.reduce((a, b) => a + b, 0));
  if (amounts.length > 0) amounts[amounts.length - 1] = round2(amounts[amounts.length - 1] + diff);
  return amounts;
};

const addDaysISO = (isoDate: string, days: number): string => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

/**
 * Crea i record invoice_installments per la fattura. Cancella eventuali rate preesistenti.
 */
export const createInstallments = async (
  invoiceId: string,
  invoiceDate: string,
  totalAmount: number,
  plan: InstallmentInput[]
): Promise<void> => {
  const err = validateInstallments(plan);
  if (err) throw new Error(err);

  await supabase.from('invoice_installments').delete().eq('invoice_id', invoiceId);

  const amounts = computeInstallmentAmounts(totalAmount, plan);
  const rows = plan.map((r, idx) => ({
    invoice_id: invoiceId,
    installment_number: idx + 1,
    percentage: r.percentage,
    amount: amounts[idx],
    due_date: addDaysISO(invoiceDate, r.due_days),
    due_days: r.due_days,
    label: r.label ?? null,
    payment_status: 'pending',
  }));

  const { error } = await supabase.from('invoice_installments').insert(rows as any);
  if (error) throw error;
};

const formatItalianDate = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('it-IT');
  } catch {
    return iso;
  }
};

/**
 * Costruisce le righe fattura dalle righe offerta (escluso detrazioni acconto).
 * Voci "a consumo": riga placeholder a 0 (fatturate a parte). Voci "canone": qty × mesi.
 */
const buildOfferInvoiceItems = (pl: any): InvoiceItemInput[] => {
  const canoneMonths: number = pl.canone_months ?? 1;
  const plItems: any[] = pl.price_list_items || [];
  const items: InvoiceItemInput[] = [];

  for (const it of plItems) {
    const product = it.products;

    if (isConsumptionCategoryItem(it)) {
      items.push({
        product_id: it.product_id ?? null,
        description: `${product?.name ?? 'Prodotto'} - ${PRICE_LIST_CONSUMPTION_LABEL} (importo variabile fatturato a parte)`,
        quantity: it.min_quantity ?? 1,
        unit: product?.unit ?? 'pz',
        unit_price: 0,
        discount_percentage: 0,
        vat_rate: product?.iva ?? 22,
      });
      continue;
    }

    const isCanone = isCanoneItem(it);
    const baseQty = it.min_quantity ?? 1;
    const qty = isCanone ? baseQty * canoneMonths : baseQty;
    const descSuffix = isCanone ? ` (canone × ${canoneMonths} mesi)` : '';

    items.push({
      product_id: it.product_id ?? null,
      description: `${product?.name ?? 'Prodotto'}${descSuffix}`,
      quantity: qty,
      unit: product?.unit ?? 'pz',
      unit_price: it.price,
      discount_percentage: it.discount_percentage ?? 0,
      vat_rate: product?.iva ?? 22,
    });
  }

  return items;
};

/**
 * Crea fattura completa da offerta: replica tutte le righe offerta in un'unica fattura,
 * senza acconto/saldo. Marca l'offerta come "invoiced".
 */
export const createFullInvoiceFromPriceList = async (params: {
  priceListId: string;
  invoiceDate: string;
  installments?: InstallmentInput[];
  status?: 'draft' | 'issued';
  iban?: string | null;
}): Promise<InvoiceRow> => {
  const { priceListId, invoiceDate } = params;

  const pl = await fetchPriceListForInvoicing(priceListId);
  if (pl.status !== 'accepted') {
    throw new Error(`Offerta in stato "${pl.status}". Solo offerte accettate possono essere fatturate.`);
  }
  if (!pl.customer_id) throw new Error('Offerta senza cliente associato.');

  const items = buildOfferInvoiceItems(pl);
  if (items.length === 0) throw new Error('Offerta senza righe da fatturare.');

  const payload: InvoicePayload = {
    customer_id: pl.customer_id,
    invoice_date: invoiceDate,
    payment_terms: pl.payment_conditions ?? null,
    notes: null,
    status: params.status ?? 'issued',
    document_type: 'invoice',
    items,
    no_vat_forfettario: !!pl.no_vat_forfettario,
    no_vat_clause_text: pl.no_vat_clause_text ?? null,
    iban: params.iban ?? null,
    causale: `Fattura Offerta "${pl.name}"`,
    price_list_id: pl.id,
  };

  const invoice = await createInvoice(payload);

  if (params.installments && params.installments.length > 0) {
    await createInstallments(invoice.id, invoiceDate, invoice.total_amount, params.installments);
  }

  await (supabase.from('price_lists') as any)
    .update({ status: 'invoiced' })
    .eq('id', pl.id);

  return invoice;
};

/**
 * Crea fattura di acconto da offerta.
 * Se `includeCanone` è true e l'offerta contiene voci canone, emette 2 righe separate:
 * "Acconto X% prodotti" + "Acconto X% canone × N mesi", così le righe sono editabili/cancellabili
 * dalla fattura prima dell'invio SDI.
 * Se `includeCanone` è false, l'acconto è calcolato solo sui prodotti (canoni esclusi).
 */
export const createDepositInvoiceFromPriceList = async (params: {
  priceListId: string;
  depositPercentage: number;
  invoiceDate: string;
  installments?: InstallmentInput[];
  status?: 'draft' | 'issued';
  iban?: string | null;
  includeCanone?: boolean;
}): Promise<InvoiceRow> => {
  const { priceListId, depositPercentage, invoiceDate } = params;
  const includeCanone = params.includeCanone !== false;
  if (depositPercentage <= 0 || depositPercentage >= 100) {
    throw new Error('Percentuale acconto deve essere tra 0 e 100 (escluso).');
  }

  const pl = await fetchPriceListForInvoicing(priceListId);
  // Consente un acconto sia da offerta "accepted" sia da una già parzialmente fatturata (più acconti).
  if (pl.status !== 'accepted' && pl.status !== 'partially_invoiced') {
    throw new Error(`Offerta in stato "${pl.status}". Solo offerte accettate possono essere fatturate.`);
  }
  if (!pl.customer_id) {
    throw new Error('Offerta senza cliente associato.');
  }

  const breakdown = computeDepositBase(pl, { includeCanone });
  if (breakdown.base <= 0) {
    throw new Error('Base imponibile = 0 (forse solo voci "a consumo"). Acconto non calcolabile.');
  }

  const canoneMonths: number = pl.canone_months ?? 1;
  const vatRate = pl.no_vat_forfettario ? 0 : 22;
  const items: InvoiceItemInput[] = [];

  const productsTaxable = round2((breakdown.products * depositPercentage) / 100);
  const canoneTaxable = round2((breakdown.canone * depositPercentage) / 100);

  if (productsTaxable > 0) {
    items.push({
      product_id: null,
      description: `Acconto ${depositPercentage}% prodotti su Offerta "${pl.name}" del ${formatItalianDate(pl.created_at)}`,
      quantity: 1,
      unit: 'pz',
      unit_price: productsTaxable,
      discount_percentage: 0,
      vat_rate: vatRate,
    });
  }

  if (includeCanone && canoneTaxable > 0) {
    items.push({
      product_id: null,
      description: `Acconto ${depositPercentage}% canone × ${canoneMonths} mesi su Offerta "${pl.name}"`,
      quantity: 1,
      unit: 'pz',
      unit_price: canoneTaxable,
      discount_percentage: 0,
      vat_rate: vatRate,
    });
  }

  if (items.length === 0) {
    throw new Error('Nessuna riga acconto da fatturare.');
  }

  const payload: InvoicePayload = {
    customer_id: pl.customer_id,
    invoice_date: invoiceDate,
    payment_terms: pl.payment_conditions ?? null,
    notes: null,
    status: params.status ?? 'issued',
    document_type: 'invoice',
    items,
    no_vat_forfettario: !!pl.no_vat_forfettario,
    no_vat_clause_text: pl.no_vat_clause_text ?? null,
    iban: params.iban ?? null,
    causale: `Acconto ${depositPercentage}% su Offerta "${pl.name}"`,
    price_list_id: pl.id,
    deposit_percentage: depositPercentage,
  };

  const invoice = await createInvoice(payload);

  if (params.installments && params.installments.length > 0) {
    await createInstallments(invoice.id, invoiceDate, invoice.total_amount, params.installments);
  }

  // Acconto: offerta fatturata SOLO in parte. Lo stato finale "invoiced" lo imposta il saldo.
  await (supabase.from('price_lists') as any)
    .update({ status: 'partially_invoiced' })
    .eq('id', pl.id);

  return invoice;
};

/**
 * Crea fattura di saldo: replica righe offerta + riga negativa detrazione acconto.
 */
export const createBalanceInvoiceFromPriceList = async (params: {
  priceListId: string;
  parentInvoiceId: string;
  invoiceDate: string;
  installments: InstallmentInput[];
  status?: 'draft' | 'issued';
  iban?: string | null;
}): Promise<InvoiceRow> => {
  const { priceListId, parentInvoiceId, invoiceDate, installments } = params;

  const pl = await fetchPriceListForInvoicing(priceListId);
  if (!pl.customer_id) throw new Error('Offerta senza cliente associato.');

  const { data: parent, error: parentErr } = await supabase
    .from('invoices')
    .select('*')
    .eq('id', parentInvoiceId)
    .single();
  if (parentErr || !parent) throw new Error('Fattura acconto di riferimento non trovata.');

  const items = buildOfferInvoiceItems(pl);

  const depositGross = Number(parent.total_amount) || 0;
  if (depositGross > 0) {
    items.push({
      product_id: null,
      description: `Detrazione acconto fattura ${parent.invoice_number ?? '#' + parent.id.slice(0, 8)}`,
      quantity: 1,
      unit: 'pz',
      unit_price: -depositGross,
      discount_percentage: 0,
      vat_rate: 0,
    });
  }

  const payload: InvoicePayload = {
    customer_id: pl.customer_id,
    invoice_date: invoiceDate,
    payment_terms: pl.payment_conditions ?? null,
    notes: null,
    status: params.status ?? 'issued',
    document_type: 'invoice',
    items,
    no_vat_forfettario: !!pl.no_vat_forfettario,
    no_vat_clause_text: pl.no_vat_clause_text ?? null,
    iban: params.iban ?? null,
    causale: `Saldo Offerta "${pl.name}" (acconto fatt. ${parent.invoice_number ?? ''})`,
    price_list_id: pl.id,
    parent_invoice_id: parent.id,
  };

  const invoice = await createInvoice(payload);

  await createInstallments(invoice.id, invoiceDate, invoice.total_amount, installments);

  // Saldo emesso: offerta ora completamente fatturata.
  await (supabase.from('price_lists') as any)
    .update({ status: 'invoiced' })
    .eq('id', pl.id);

  return invoice;
};

/**
 * Ricalcola gli importi delle rate di una fattura in base al nuovo totale,
 * preservando percentuali, gg, etichetta e stato pagamento.
 * Se anche una sola rata risulta `paid`, l'operazione viene saltata (skipped: true).
 * Restituisce { updated: boolean, skipped: boolean, reason?: string }.
 */
export const recomputeInstallmentAmounts = async (
  invoiceId: string,
  totalAmount: number,
  invoiceDate: string
): Promise<{ updated: boolean; skipped: boolean; reason?: string }> => {
  const { data: rows, error } = await supabase
    .from('invoice_installments')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('installment_number', { ascending: true });
  if (error) throw error;
  if (!rows || rows.length === 0) return { updated: false, skipped: true, reason: 'no_installments' };

  if (rows.some((r: any) => r.payment_status === 'paid')) {
    return { updated: false, skipped: true, reason: 'has_paid_installments' };
  }

  const plan: InstallmentInput[] = rows.map((r: any) => ({
    percentage: Number(r.percentage),
    due_days: Number(r.due_days ?? 0),
    label: r.label ?? null,
  }));
  const amounts = computeInstallmentAmounts(totalAmount, plan);

  for (let i = 0; i < rows.length; i++) {
    const r: any = rows[i];
    const newDue = addDaysISO(invoiceDate, Number(r.due_days ?? 0));
    const { error: updErr } = await (supabase.from('invoice_installments') as any)
      .update({ amount: amounts[i], due_date: newDue })
      .eq('id', r.id);
    if (updErr) throw updErr;
  }
  return { updated: true, skipped: false };
};

/**
 * Carica le rate di una fattura ordinate per numero.
 */
export const fetchInstallments = async (invoiceId: string) => {
  const { data, error } = await supabase
    .from('invoice_installments')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('installment_number', { ascending: true });
  if (error) throw error;
  return data ?? [];
};

/**
 * Aggiorna stato pagamento singola rata.
 */
export const markInstallmentPaid = async (
  installmentId: string,
  paid: boolean
): Promise<void> => {
  const { error } = await (supabase.from('invoice_installments') as any)
    .update({
      payment_status: paid ? 'paid' : 'pending',
      paid_at: paid ? new Date().toISOString() : null,
    })
    .eq('id', installmentId);
  if (error) throw error;
};
